import React, { Component, Fragment } from 'react'
import { withRouter } from '@/checkout-instore-legacy/utils/withRouter'
import { FormattedMessage, injectIntl } from 'react-intl'
import pick from 'lodash/pick'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import flatten from 'lodash/flatten'
import ScheduledDeliveryModal from '@vtexlab/scheduled-delivery'
import Layout from '@vtex/styleguide/lib/Layout'
import CartLocation from '@/checkout-instore-legacy/cart/containers/CartLocationContainer'

import SummaryView from '@/checkout-instore-legacy/components/Commons/Summary/containers/SummaryViewContainer'
import FooterButton from '@/checkout-instore-legacy/components/Commons/Elements/FooterButton'
import withOrderFormAndVendor from '@/checkout-instore-legacy/order/withOrderFormAndVendor'
import { DEFAULT_GROUP_PACKAGE_OPTIONS } from '@/checkout-instore-legacy/package/constants'
import {
  selectCheapestSlaForNonTakeAwayItems,
  selectFastestSlaForNonTakeAwayItems,
  selectPickup,
  getNewLogisticsInfoWithSelectedScheduled,
  getSlaFlagsFromLogisticsInfo,
  getOrderAsPackages,
  validatePackagesWithValidSlas,
  filterLogisticsInfo,
  changeSelectedSla,
  isTakeAwaySlaType,
  isDeliverySlaType,
  isPickupSlaType,
} from '@/checkout-instore-legacy/shipping/utils'
import {
  SLA_SELECTOR_TYPES,
  PossibleSlaTypes,
} from '@/checkout-instore-legacy/shipping/constants'

import ChooseSelectorType, { Flags } from './ChooseSelectorType'

import withOrderFormNotEmpty from '@/checkout-instore-legacy/order/withOrderFormNotEmpty'
import {
  PackageCard,
  Package,
} from '@/checkout-instore-legacy/shipping/components/SlaSelector/PackageCard'
import Loader from '@/checkout-instore-legacy/components/Commons/Loader'

export type SlaSelectorProps = {
  orderForm: OrderForm
  locationLogisticsInfo: any
  shipping: {
    logisticsInfo: LogisticsInfo
    loading: boolean
    address: {
      postalCode: string
    }
    sellers?: Seller[]
  }
  packages: {
    delivery: Package[]
    'pickup-in-point': Package[]
    'take-away': Package[]
  }
  router: any
  intl: IntlObject
  sendLogisticsInfoOnShippingData: (
    logisticsInfo: LogisticsInfo,
    id?: LogisticsInfoItem['itemId']
  ) => void
  sendNewScheduledDeliveryChoice: (args: any) => void
  isOnline?: boolean
  loadSellerById: (sellerId: string) => void
}

type ModalCloseState = {
  showScheduledDeliveryModal: boolean
  selectedScheduledPkg: Package | null
  selectedPickupPkg: Package | null
  activePickupPoint: Package | null
}

type SlaSelectorState = Flags &
  ModalCloseState & {
    selectedSla?: SLA
    selectedDate?: any
    selectedTime?: string
  }

const EmptyPageHeaderComponent = () => null
const EMPTY_PAGE_HEADER = <EmptyPageHeaderComponent />

class SlaSelector extends Component<SlaSelectorProps, SlaSelectorState> {
  modalClosedState = {
    showScheduledDeliveryModal: false,
    selectedScheduledPkg: null,
    selectedPickupPkg: null,
    activePickupPoint: null,
  }

  constructor(props: SlaSelectorProps) {
    super(props)

    this.state = {
      ...getSlaFlagsFromLogisticsInfo(
        props.shipping.logisticsInfo,
        props.orderForm.checkedInPickupPointId
      ),
      ...this.modalClosedState,
    }
  }

  componentDidMount() {
    const sellerList = flatten(
      this.props.orderForm.items.map((item) => item.sellerChain)
    )
    // TO DO: Study the possibility of sending an list of ids to graphql
    sellerList.forEach((sellerId) => this.props.loadSellerById(sellerId))
  }

  componentDidUpdate(prevProps: SlaSelectorProps) {
    if (
      !isEqual(
        prevProps.shipping.logisticsInfo,
        this.props.shipping.logisticsInfo
      )
    ) {
      this.setState(
        getSlaFlagsFromLogisticsInfo(
          this.props.shipping.logisticsInfo,
          this.props.orderForm.checkedInPickupPointId
        )
      )
    }
  }

  handleClickFooterButton = () => {
    this.props.router.push('/shipping-address')
  }

  handleClickCheapest = () => {
    const { isCheapestSelected } = this.state
    const { orderForm } = this.props
    if (isCheapestSelected) {
      return
    }
    // const orderForm = getOrderForm()
    const newLogisticsInfo = selectCheapestSlaForNonTakeAwayItems(orderForm)
    this.props.sendLogisticsInfoOnShippingData(newLogisticsInfo)
  }

  handleClickFastest = () => {
    const { orderForm } = this.props

    const { isFastestSelected } = this.state
    if (isFastestSelected) {
      return
    }
    // const orderForm = getOrderForm()
    const newLogisticsInfo = selectFastestSlaForNonTakeAwayItems(orderForm)
    this.props.sendLogisticsInfoOnShippingData(newLogisticsInfo)
  }

  handleClickScheduled = () => {
    const { isScheduledDeliverySelected } = this.state
    if (isScheduledDeliverySelected) {
      return
    }
    const { logisticsInfo } = this.props.shipping
    const newLogisticsInfo = getNewLogisticsInfoWithSelectedScheduled(
      logisticsInfo
    )

    this.props.sendLogisticsInfoOnShippingData(newLogisticsInfo)
  }

  handleClickPickup = () => {
    const { isPickupSelected } = this.state
    if (isPickupSelected) {
      return
    }

    const { locationLogisticsInfo } = this.props

    const newLogisticsInfo = selectPickup(locationLogisticsInfo)

    if (
      !isEmpty(newLogisticsInfo) &&
      !isEqual(newLogisticsInfo, locationLogisticsInfo)
    ) {
      this.props.sendLogisticsInfoOnShippingData(newLogisticsInfo)
    }
  }

  handleSelectorTypeChanged = (
    newFlags: Partial<Flags>,
    type: PossibleSlaTypes
  ) => {
    switch (type) {
      case SLA_SELECTOR_TYPES.cheapest:
        this.handleClickCheapest()
        break
      case SLA_SELECTOR_TYPES.fastest:
        this.handleClickFastest()
        break
      case SLA_SELECTOR_TYPES.scheduled:
        this.handleClickScheduled()
        break
      case SLA_SELECTOR_TYPES.pickup:
        this.handleClickPickup()
        break
      default:
        break
    }

    this.setState(newFlags)
  }

  handleSlaSelectionForProduct = (index: number, sla: SLA) => {
    const {
      shipping: { logisticsInfo },
    } = this.props
    logisticsInfo[index] = changeSelectedSla(logisticsInfo[index], sla)
    this.props.sendLogisticsInfoOnShippingData(
      logisticsInfo,
      logisticsInfo[index].itemId
    )
  }

  handleShowScheduledDeliveryModal = (pkg: Package) => {
    this.setState({
      showScheduledDeliveryModal: true,
      selectedScheduledPkg: pkg,
    })
  }

  handleShowPickupPointsModal = (pkg: Package) => {
    this.setState({
      selectedPickupPkg: pkg,
    })
  }

  handleClosePickupPointsModal = () => {
    this.setState({
      selectedPickupPkg: null,
    })
  }

  handleCloseScheduledDeliveryModal = () => {
    this.setState({
      showScheduledDeliveryModal: false,
    })
  }

  handleChangeScheduledDeliveryModal = ({
    selectedSla,
    selectedDate,
    selectedTime,
  }: Partial<SlaSelectorState>) => {
    this.setState({
      selectedSla,
      selectedDate,
      selectedTime,
    })
  }

  handleConfirmScheduledDeliveryModal = ({
    deliveryWindow,
    selectedSla,
  }: {
    deliveryWindow: any
    selectedSla: SLA
  }) => {
    const pkg = this.state.selectedScheduledPkg
    if (pkg === null) return
    const { items } = pkg
    this.props.sendNewScheduledDeliveryChoice({
      items,
      deliveryWindow,
      selectedSla,
    })
  }

  filterTakeAwayPackage(packages: Package[]) {
    return packages.filter((pkg) => !isTakeAwaySlaType(pkg.selectedSlaType))
  }

  filterDeliveryPackage(packages: Package[]) {
    return packages.filter((pkg) => isDeliverySlaType(pkg.selectedSlaType))
  }

  filterPickupPackage(packages: Package[]) {
    return packages.filter((pkg) => isPickupSlaType(pkg.selectedSlaType))
  }

  renderFooterButton = () => {
    const { shipping, orderForm, isOnline = true } = this.props

    const packages = getOrderAsPackages(
      orderForm,
      DEFAULT_GROUP_PACKAGE_OPTIONS
    )
    const invalidPackages = validatePackagesWithValidSlas(packages)
    const disabled = invalidPackages.length > 0 || shipping.loading || !isOnline

    return (
      <FooterButton
        id="go-to-address"
        className="absolute-l bottom-0-l right-0-l w-25-l lh-copy-l"
        onClick={this.handleClickFooterButton}
        disabled={disabled}
        isLoading={shipping.loading}
        kind="success"
      >
        <FormattedMessage id="shippinggoToAddressButton" />
      </FooterButton>
    )
  }

  render() {
    const { shipping, intl, orderForm, packages } = this.props
    const {
      address: { postalCode },
      logisticsInfo,
      sellers,
    } = shipping

    const { selectedScheduledPkg, showScheduledDeliveryModal } = this.state

    const flags = pick(this.state, [
      'shouldCheapestExist',
      'shouldFastestExist',
      'shouldScheduledDeliveryExist',
      'shouldPickupExist',
      'shouldCustomExist',
      'isPickupOnlyLogisticsInfo',
      'isCheapestSelected',
      'isFastestSelected',
      'isScheduledDeliverySelected',
      'isPickupSelected',
      'isCustomSelected',
    ])

    const scheduledDeliveryPkgLogisticsInfo =
      selectedScheduledPkg && selectedScheduledPkg.items
        ? filterLogisticsInfo(logisticsInfo, {
            items: selectedScheduledPkg.items,
          })
        : null

    const packageCardProps = {
      logisticsInfo: logisticsInfo,
      onSlaSelection: this.handleSlaSelectionForProduct,
      flags: flags,
      postalCode: postalCode,
      onSchedulePackage: this.handleShowScheduledDeliveryModal,
      onSelectPickupPoint: this.handleShowPickupPointsModal,
      intl: intl,
      sellers,
    }

    const allPackagesEmpty =
      isEmpty(packages) ||
      (isEmpty(packages['delivery']) &&
        isEmpty(packages['take-away']) &&
        isEmpty(packages['pickup-in-point']))

    return (
      <Fragment>
        {/* @ts-ignore ts-migrate(2322) FIXME: Type '{ children: Element[]; renderFooterButton: (... Remove this comment to see the full error message */}
        <SummaryView renderFooterButton={this.renderFooterButton}>
          <div className="bg-base bb b--muted-4  pv3 ph5 ph7-ns">
            <div className="flex truncate bn-ns ">
              <CartLocation context="Shipping Page" />
            </div>
          </div>
          <div className="flex-auto bg-light-silver overflow-auto momentum ">
            <Layout fullWidth pageHeader={EMPTY_PAGE_HEADER}>
              <div>
                <div className="mt7 mt6-s mh5-s mh0-ns">
                  <h2 className="truncate ma0 mb4 f3 f2-ns lh-copy c-on-base">
                    <FormattedMessage id="shippingSlaSelectorLabel" />
                  </h2>

                  <ChooseSelectorType
                    flags={flags}
                    onSelectorTypeChanged={this.handleSelectorTypeChanged}
                  />
                </div>

                {allPackagesEmpty && shipping.loading && <Loader loading />}

                <div className="flex flex-column ">
                  {packages['delivery'].map((pkg, index) => (
                    <div className="mv3" key={index}>
                      <PackageCard
                        type="delivery"
                        slaPackage={pkg}
                        index={index}
                        {...packageCardProps}
                      />
                    </div>
                  ))}
                </div>

                <div className="flex flex-column ">
                  {packages['pickup-in-point'].map((pkg, index) => (
                    <div className="mv3" key={index}>
                      <PackageCard
                        type="pickup-in-point"
                        slaPackage={pkg}
                        index={index}
                        {...packageCardProps}
                      />
                    </div>
                  ))}
                </div>

                <div className="flex flex-column ">
                  {packages['take-away'].map((pkg, index) => (
                    <div className="mv3" key={index}>
                      <PackageCard
                        type="take-away"
                        slaPackage={pkg}
                        index={index}
                        {...packageCardProps}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </Layout>
          </div>
        </SummaryView>
        {selectedScheduledPkg && (
          <ScheduledDeliveryModal
            title={intl.formatMessage(
              {
                id: 'shippingSchedulingTitle',
              },
              {
                // @ts-expect-error ts-migrate(2339) FIXME: Property 'index' does not exist on type 'Package'.
                packageIndex: selectedScheduledPkg.index + 1,
              }
            )}
            visible={showScheduledDeliveryModal}
            items={selectedScheduledPkg.items}
            logisticsInfo={scheduledDeliveryPkgLogisticsInfo}
            storePreferencesData={orderForm.storePreferencesData}
            deliveryWindow={selectedScheduledPkg.deliveryWindow}
            selectedSla={selectedScheduledPkg.selectedSla}
            onClose={this.handleCloseScheduledDeliveryModal}
            onChange={this.handleChangeScheduledDeliveryModal}
            onConfirm={this.handleConfirmScheduledDeliveryModal}
            locale={intl.locale}
          />
        )}
      </Fragment>
    )
  }
}

export default injectIntl(
  withRouter(withOrderFormAndVendor(withOrderFormNotEmpty(SlaSelector)))
)
